import type React from "react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, Alert, LinearProgress, Chip } from "@mui/material";
import { EnhancedDialogTitle } from "Common/Components";

interface IAbsorbLmsRedirectDialogProps {
  open: boolean;
  userEmail: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const COUNTDOWN_SECONDS = 10;

export const AbsorbLmsRedirectDialog: React.FC<IAbsorbLmsRedirectDialogProps> = ({
  open,
  userEmail,
  onConfirm,
  onCancel,
}) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!open) {
      setCountdown(COUNTDOWN_SECONDS);
      return;
    }

    if (countdown === 0) {
      onConfirm();
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [open, countdown, onConfirm]);

  const progress = ((COUNTDOWN_SECONDS - countdown) / COUNTDOWN_SECONDS) * 100;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          variant: "popup",
          sx: {
            padding: 0,
          },
        },
      }}
    >
      <EnhancedDialogTitle title="Account Not Found" onClose={onCancel} disableClose={false} />

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">
            {"We couldn't find an account at Middleby University associated with "}
            <strong>{userEmail}</strong>
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {"Don't worry! You'll be automatically redirected to the registration page where you can create a new account."}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Redirecting in
          </Typography>
          <Chip
            label={`${countdown}s`}
            color="primary"
            sx={(theme) => ({
              fontSize: theme.typography.body1.fontSize,
              minWidth: 60,
            })}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="text">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained">
          Register Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};
